<template>
  <div>
    <template v-if="state == STATES.choose">
      <div class="v-btn v-btn--contained v-btn--router theme--light v-size--default">
        <input type="file" accept="image/jpeg" id="textrecog-file" ref="file" @change="imageTaken"/>
        <label for="textrecog-file" class="v-btn__content">
          <v-icon>mdi-camera</v-icon>
          <span>Take or choose picture</span>
        </label>
      </div>
    </template>
    <template v-else-if="state == STATES.loading">
      <v-progress-circular
        :size="50"
        color="primary"
        indeterminate
      ></v-progress-circular>
    </template>
    <template v-else-if="state == STATES.crop">
      <div class="">
        <div class="image-wrapper" ref="imageWrapper">
          <img v-if="image"
            :src="image" class="image"
            ref="image"
            @load="imageLoaded"
          />
          <VueDragResize v-if="imageReady" class="image-rect"
            :parentLimitation="true"
            :w="imageWidth"
            :h="imageHeight"
            :minh="10"
            :minw="10"
            :isActive="imageReady && annotating"
            @resizing="resizeRect"
            @dragging="resizeRect"
          />
        </div>
      </div>
      <v-row align="center" justify="center">
        <v-col class="d-flex" cols="12" sm="6">
          <v-select
            :items="languages"
            v-model="language"
            @change="languageChanged"
            label="Language"
          ></v-select>
        </v-col>
      </v-row>
      <v-row align="center" justify="center">
        <v-col class="d-flex" cols="12" sm="6">
          <v-btn block @click="startOCR" :loading="!canOCR" :disabled="!canOCR" large color="primary">
            Continue
          </v-btn>
        </v-col>
      </v-row>
    </template>
    <template v-else-if="state == STATES.ocr">
      <v-progress-linear :value="progress"></v-progress-linear>
    </template>
  </div>
</template>

<script>

import EXIF from 'exif-js'

import { createWorker } from 'tesseract.js'

import VueDragResize from 'vue-drag-resize'

import { cleanOCRText } from '../lib/utils.js'

const STATES = Object.freeze({
  choose: 0,
  loading: 1,
  crop: 2,
  ocr: 3
})

const LANGUAGES = ['en', 'de']
const TESSERACT_MAPPING = {
  'en': 'eng',
  'de': 'deu'
}

export default {
  name: 'TextRecogniser',
  props: {
    quoteImage: {
      required: false,
      default: null
    }
  },
  components: {
    VueDragResize
  },
  created () {
    this.STATES = STATES
    const isProd = process.env.NODE_ENV === 'production'
    let workerOptions = {
      logger: (m) => {
        if (m.status !== 'recognizing text') { return }
        this.progress = m.progress * 100
      }
    }
    if (isProd) {
      workerOptions = {
        workerPath: 'js/tesseract.worker.min.js',
        corePath: 'js/tesseract-core.wasm.js',
        langPath: 'data',
        ...workerOptions
      }
    }
    this.worker = createWorker(workerOptions)
    // Called as early as possible
    this.worker.load().then(this.initOCR)
  },
  destroyed () {
    if (this.image !== null) {
      window.URL.revokeObjectURL(this.image)
    }
  },
  data () {
    return {
      text: '',
      progress: 0,
      state: STATES.choose,
      ocrReady: false,
      imageReady: false,
      image: null,
      annotationRect: null,
      width: null,
      height: null,
      imageWidth: 100,
      imageHeight: 100,
      annotating: false,
      croppedImage: null,
      language: 'en',
      loadedLanguages: {},
      ocrPromise: null
    }
  },
  mounted () {
    if (this.quoteImage) {
      this.setupImage(this.quoteImage)
    }
  },
  computed: {
    canOCR () {
      return this.ocrReady && this.imageReady
    },
    languages () {
      return LANGUAGES
    },
    currentTesseractLanguage () {
      return TESSERACT_MAPPING[this.language]
    },
    supportsExifOrientation () {
      return true
    }
  },
  methods: {
    initOCR () {
      const lang = this.currentTesseractLanguage
      this.ocrPromise = this.loadLanguage(lang)
        .then(() => this.initLanguage(lang))
        .then(() => {
          this.ocrReady = true
          this.ocrPromise = Promise.resolve()
        })
      return this.ocrPromise
    },
    initLanguage (lang) {
      if (this.loadedLanguage === lang) {
        return Promise.resolve()
      }
      return this.worker.initialize(lang)
        .then(() => {
          this.loadedLanguage = lang
        })
    },
    loadLanguage (lang) {
      if (this.loadedLanguages[lang]) {
        return Promise.resolve()
      }
      return this.worker.loadLanguage(lang)
        .then(() => {
          this.loadedLanguages[lang] = true
        })
    },
    startOCR () {
      this.changeState(STATES.ocr)
      const image = this.$refs.image
      const ratio = this.width / image.clientWidth
      let rect = {
        left: this.rect.left * ratio,
        top: this.rect.top * ratio,
        width: this.rect.width * ratio,
        height: this.rect.height * ratio
      }
      this.cropImage(image, rect).then((blob) => {
        this.$emit('preview', blob)
        this.recogniseText(blob)
      })
    },
    imageTaken () {
      let image = this.$refs.file.files[0]
      return this.setupImage(image)
    },
    setupImage (image) {
      this.changeState(STATES.loading)
      this.prepareImage(image).then((result) => {
        this.width = result.width
        this.height = result.height
        this.setImage(result.blob)
        this.annotating = true
        this.changeState(STATES.crop)
      })
    },
    setImage (blob) {
      if (this.image !== null) {
        window.URL.revokeObjectURL(this.image)
      }
      this.image = window.URL.createObjectURL(blob)
    },
    recogniseText (image) {
      this.initOCR()
        .then(() => this.worker.recognize(image))
        .then((result) => {
          this.$emit('hastext', cleanOCRText(result.data.text))
        })
    },
    prepareImage (image) {
      return new Promise((resolve) => {
        const domImage = new window.Image()
        domImage.addEventListener('load', () => {
          this.getExifData(domImage).then((config) => {
            this.rotateImage(domImage, config).then((result) => {
              resolve(result)
            })
          })
        }, false)
        domImage.src = window.URL.createObjectURL(image)
      })
    },
    rotateImage (img, config) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        if (config.degree % 180 === 0) {
          canvas.width = config.width
          canvas.height = config.height
        } else {
          canvas.width = config.height
          canvas.height = config.width
        }
        const ctx = canvas.getContext('2d')
        if (this.supportsExifOrientation) {
          ctx.clearRect(0, 0, canvas.width, canvas.height)
          ctx.save()

          ctx.translate(canvas.width / 2, canvas.height / 2)
          ctx.rotate(config.degree * Math.PI / 180)
          ctx.drawImage(img, -img.width / 2, -img.height / 2)

          ctx.restore()
        } else {
          ctx.drawImage(img, 0, 0)
        }
        let imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
        imageData = this.increaseContrast(imageData, 100)
        ctx.putImageData(imageData, 0, 0)
        canvas.toBlob((b) => resolve({ blob: b, width: canvas.width, height: canvas.height }), 'image/jpeg', 1.0)
      })
    },
    cropImage (image, rect) {
      return new Promise((resolve) => {
        const canvas = document.createElement('canvas')
        const sourceX = rect.left
        const sourceY = rect.top
        const sourceWidth = rect.width
        const sourceHeight = rect.height
        const destWidth = sourceWidth
        const destHeight = sourceHeight
        const destX = 0
        const destY = 0
        canvas.width = sourceWidth
        canvas.height = sourceHeight
        const ctx = canvas.getContext('2d')
        ctx.drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight)
        canvas.toBlob((b) => resolve(b), 'image/jpeg', 1.0)
      })
    },
    increaseContrast (imgData, contrast) { // input range [-100..100]
      var d = imgData.data
      contrast = (contrast / 100) + 1 // convert to decimal & shift range: [0..2]
      var intercept = 128 * (1 - contrast)
      for (var i = 0; i < d.length; i += 4) { // r,g,b,a
        d[i] = d[i] * contrast + intercept
        d[i + 1] = d[i + 1] * contrast + intercept
        d[i + 2] = d[i + 2] * contrast + intercept
      }
      return imgData
    },
    getExifData (image) {
      let width = image.width
      let height = image.height
      if (this.supportsExifOrientation) {
        return Promise.resolve({
          exif: true,
          width,
          height,
          degree: 0
        })
      }
      return new Promise((resolve) => {
        EXIF.getData(image, function () {
          let data = {
            exif: true,
            width,
            height
          }
          let orientation = EXIF.getTag(this, 'Orientation')
          if (orientation === 6) {
            data.degree = 90
          } else if (orientation === 8) {
            data.degree = 270
          } else if (orientation === 3) {
            data.degree = 180
          }
          resolve(data)
        })
      })
    },
    imageLoaded () {
      this.imageReady = true
      this.imageWidth = this.$refs.image.clientWidth
      this.imageHeight = this.$refs.image.clientHeight
      this.rect = {
        left: 0,
        top: 0,
        width: this.imageWidth,
        height: this.imageHeight
      }
    },
    resizeRect (newRect) {
      this.rect = newRect
    },
    languageChanged () {
      this.initOCR()
    },
    changeState (state) {
      this.state = state
      this.$emit('statechanged', state)
    }
  }
}
</script>

<style scoped>
.image-wrapper {
  position: relative;
  display: inline-block;
}
.image {
  max-width: 100%;
  max-height: 90vh;
  pointer-events: none;
  image-orientation: from-image;
}
.image-rect {
  border: 3px solid red;
}

#textrecog-file {
  border: 0;
  clip: rect(0, 0, 0, 0);
  height: 1px;
  overflow: hidden;
  padding: 0;
  position: absolute !important;
  white-space: nowrap;
  width: 1px;
}

#textrecog-file + label {
  cursor: pointer
}

</style>
